<template>
  <div class="medication-table">
    <DataTable
      v-if="items.length"
      :className="tableClassName"
      :headers="headers"
      :items="tableItems"
      @delete="deleteMedication"
    />
    <div class="medication-table__title">{{ $t("visits.schedule.second.addMedications") }}:</div>
    <div class="medications-table_request-visit" v-if="this.place === 'checkout'">
      <v-row dense>
        <v-col cols="12" md="8">
          <BaseInput
            v-model="name"
            fieldName="name"
            className="bd-grey"
            :errors="nameErrors"
            :placeholder="$t('visits.medical.medicationPlaceholder')"
            @change="onFieldChange"
            @blur="$v.name.$touch()"
          />
        </v-col>
        <v-col cols="12" md="4">
          <DatePicker
            v-model="startDate"
            :placeholder="$t('visits.medical.startDatePlaceholder')"
            :height="47"
            :errors="startDateErrors"
            :maxDate="new Date().toISOString().substr(0, 10)"
            :dense="true"
            @blur="$v.startDate.$touch()"
            @setDate="onStartDateChange"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="6" md="3">
          <BaseInput
            v-model="dosage"
            :errors="dosageErrors"
            fieldName="dosage"
            :placeholder="$t('visits.summary.dosage')"
            @change="onFieldChange"
            @blur="$v.dosage.$touch()"
          />
        </v-col>
        <v-col cols="6" md="4">
          <Select
            v-model="dosageType"
            fieldName="dosageType"
            :items="typesList"
            :label="$t('visits.summary.units')"
            :errors="dosageErrors"
            @change="onFieldChange"
            @blur="$v.dosage.$touch()"
          />
        </v-col>
        <v-col cols="12" md="5">
          <Select
            v-model="dosageFrequency"
            fieldName="dosageFrequency"
            :items="frequencyList"
            :label="$t('visits.summary.frequency')"
            :errors="dosageErrors"
            @change="onFieldChange"
            @blur="$v.dosage.$touch()"
          />
        </v-col>
      </v-row>
      <OutlinedButton
        additionalClassName="outlined-btn__green add-medication"
        :fullWidth="true"
        :text="$t('visits.medical.addMedication')"
        size="large"
        @onClick="addMedication"
      />
    </div>
    <div class="medications-table_visit-notes" v-else>
      <v-row dense>
        <v-col cols="12" md="3"
          ><BaseInput
            v-model="name"
            fieldName="name"
            :errors="nameErrors"
            :placeholder="$t('visits.medical.medicationPlaceholder')"
            @change="onFieldChange"
            @blur="$v.name.$touch()"
        /></v-col>
        <v-col cols="6" md="1">
          <BaseInput
            v-model="dosage"
            :errors="dosageErrors"
            fieldName="dosage"
            :placeholder="$t('visits.summary.dose')"
            @change="onFieldChange"
            @blur="$v.dosage.$touch()"
          />
        </v-col>
        <v-col cols="6" md="2"
          ><Select
            v-model="dosageType"
            fieldName="dosageType"
            :items="typesList"
            :label="$t('visits.summary.unit')"
            :errors="dosageErrors"
            @change="onFieldChange"
            @blur="$v.dosage.$touch()"
        /></v-col>
        <v-col cols="12" md="2">
          <Select
            v-model="dosageFrequency"
            fieldName="dosageFrequency"
            :items="frequencyList"
            :label="$t('visits.summary.frequency')"
            :errors="dosageErrors"
            @change="onFieldChange"
            @blur="$v.dosage.$touch()"
          />
        </v-col>
        <v-col cols="12" md="2">
          <DatePicker
            v-model="startDate"
            :placeholder="$t('visits.medical.startDatePlaceholder')"
            :height="47"
            :errors="startDateErrors"
            :maxDate="new Date().toISOString().substr(0, 10)"
            :dense="true"
            @blur="$v.startDate.$touch()"
            @setDate="onStartDateChange"
          />
        </v-col>
        <v-col cols="12" md="2">
          <OutlinedButton
            additionalClassName="outlined-btn__green add-medication"
            :fullWidth="false"
            :text="$t('visits.medical.addMedication')"
            size="large"
            @onClick="addMedication"
        /></v-col>
      </v-row>
    </div>
  </div>
</template>
<script>
import { DateTime } from "luxon";
import shortid from "shortid";
import { required } from "vuelidate/lib/validators";

import DataTable from "@/components/shared/DataTable";
import DatePicker from "@/components/shared/DatePicker";
import BaseInput from "@/components/uikit/BaseInput";
import OutlinedButton from "@/components/uikit/OutlinedButton";
import Select from "@/components/uikit/Select";

export default {
  name: "MedicationTable",
  props: ["items", "place", "tableClassName"],
  components: {
    DataTable,
    DatePicker,
    BaseInput,
    Select,
    OutlinedButton,
  },
  data: () => ({
    name: "",
    dosage: "",
    dosageType: "",
    dosageFrequency: "",
    startDate: null,
  }),
  validations: {
    name: { required },
    startDate: { required },
    dosage: { required },
  },
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.name.$dirty) return errors;
      const field = this.$t("visits.medical.name");
      !this.$v.name.required && errors.push(this.$t("validation.required", { field }));
      return errors;
    },
    startDateErrors() {
      const errors = [];
      if (!this.$v.startDate.$dirty) return errors;
      const field = this.$t("visits.summary.startDate");
      !this.$v.startDate.required && errors.push(this.$t("validation.required", { field }));
      return errors;
    },
    dosageErrors() {
      const errors = [];
      if (!this.dosage || !this.dosageType || this.dosageFrequency) {
        if (!this.$v.dosage.$dirty) return errors;
      }
      const field = this.$t("visits.summary.dosage");
      !this.$v.dosage.required && errors.push(this.$t("validation.required", { field }));
      return errors;
    },
    headers() {
      return [
        {
          text: this.$t("visits.summary.medication"),
          value: "name",
          width: 150,
        },
        {
          text: this.$t("visits.summary.dose"),
          value: "dosage",
          width: 150,
          separator: ",",
        },
        {
          text: this.$t("visits.summary.startDate"),
          value: "startDate",
        },
        { text: "", value: "action" },
      ];
    },
    tableItems() {
      return this.items.map((item) => {
        const dosageFrequency = this.$t("general.medicationFrequency")[item.dosageFrequency] || item.dosageFrequency;
        const dosageType = this.$t("general.medicationTypes")[item.dosageType] || item.dosageType;
        return {
          id: item.id,
          name: item.name,
          dosage:
            !item.dosage || !item.dosageType || !item.dosageFrequency
              ? "-"
              : `${item.dosage} ${dosageType}, ${dosageFrequency}`,
          startDate: DateTime.fromJSDate(item.startDate).toFormat("M/d/yyyy"),
        };
      });
    },
    typesList() {
      const medicationTypes = this.$t("general.medicationTypes");
      if (!medicationTypes) {
        return [];
      }
      return Object.keys(medicationTypes).map((key) => ({ text: medicationTypes[key], value: key }));
    },
    frequencyList() {
      const medicationFrequency = this.$t("general.medicationFrequency");
      if (!medicationFrequency) {
        return [];
      }
      return Object.keys(medicationFrequency).map((key) => ({ text: medicationFrequency[key], value: key }));
    },
  },
  methods: {
    onFieldChange(value, fieldName) {
      this[fieldName] = value;
    },
    onStartDateChange(value) {
      this.startDate = value;
    },
    deleteMedication(id) {
      const newItems = this.items.filter((i) => i.id !== id);
      this.$emit("change", newItems);
    },
    addMedication() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      const newItems = this.items.concat([
        {
          id: shortid.generate(),
          name: this.name.trim(),
          dosage: this.dosage.trim(),
          dosageType: this.dosageType,
          dosageFrequency: this.dosageFrequency,
          startDate: this.startDate,
        },
      ]);
      this.name = "";
      this.dosage = "";
      this.dosageType = "";
      this.dosageFrequency = "";
      this.startDate = null;
      this.$v.$reset();
      this.$emit("change", newItems);
    },
  },
};
</script>

<style lang="scss">
.medication-table {
  .v-input__slot {
    border: 1px solid rgba(51, 52, 62, 0.4) !important;
  }
  &__title {
    font-weight: bold;
    padding: 20px 20px 0;
    color: $primaryblack2;
  }
}
.add-medication {
  background-color: #fff !important;
}
</style>
